import React from 'react';
import { LotState, ClosedLotStatus, closedLotWord } from '../../utils/saleState';
import { formatTimeRemaining } from '../../utils/lotTiming';

/**
 * The countdown / status chip on a lot tile.
 *
 * PURE AND TIMERLESS BY DESIGN. It holds no state and starts no interval — it
 * renders whatever LotState it is handed. The single page-level clock
 * (hooks/useAuctionClock) drives every instance. This is what makes a 138-lot
 * grid affordable, and it is why this exists alongside (not instead of)
 * components/auction/CountdownTimer.tsx, which owns its own interval and stays
 * in use on LotDetail.
 *
 * It also never claims more than it knows: a lot whose clock has run out renders
 * "Closing…" until saleState's settling grace expires. See the long note in
 * utils/saleState.ts for why.
 */

interface LotClockProps {
  state: LotState;
  /** Tiles use 'tile'; a denser row (results list) uses 'inline'. */
  variant?: 'tile' | 'inline';
  className?: string;
  /** closedLotStatus(auction, lot): 'closed' | 'under-review' | 'to-be-relisted'. Display only. */
  closedStatus?: ClosedLotStatus;
}

export const LotClock: React.FC<LotClockProps> = ({ state, variant = 'tile', className = '', closedStatus = 'closed' }) => {
  const mono = variant === 'tile' ? 'text-xs font-mono' : 'text-[11px] font-mono';

  switch (state.kind) {
    case 'unknown':
      // No end time on the lot — an imported-but-never-armed sale. Say nothing
      // rather than render a fake 0:00.
      return <span className={`${mono} text-gray-400 ${className}`}>—</span>;

    case 'upcoming':
      return (
        <span className={`${mono} text-gray-600 ${className}`} title={state.endsAt?.toLocaleString()}>
          {formatTimeRemaining(state.secondsRemaining)}
        </span>
      );

    case 'closing_soon':
      return (
        <span
          className={`${mono} font-bold text-amber-600 ${className}`}
          title={state.endsAt?.toLocaleString()}
        >
          {formatTimeRemaining(state.secondsRemaining)}
          {state.extended && <span className="ml-1 font-sans font-semibold">·EXT{state.extensionCount}</span>}
        </span>
      );

    case 'closing_now':
      return (
        <span
          className={`${mono} font-bold text-red-600 animate-pulse ${className}`}
          title={state.endsAt?.toLocaleString()}
          aria-live="off"
        >
          {formatTimeRemaining(state.secondsRemaining)}
          {state.extended && <span className="ml-1 font-sans font-semibold">·EXT{state.extensionCount}</span>}
        </span>
      );

    case 'settling':
      // Deliberately non-committal. A last-second bid may have extended this lot
      // and the snapshot carrying the new end time may still be in flight.
      return (
        <span className={`${mono} font-bold text-red-600 ${className}`} aria-live="off">
          Closing…
        </span>
      );

    case 'closed_sold':
    case 'closed_unsold':
      return <span className={`${mono} text-gray-500 ${className}`}>{closedLotWord(closedStatus, 'lower')}</span>;

    default:
      return null;
  }
};

/**
 * Corner badge for the image area: EXTENDED / CLOSING / CLOSED.
 * Same state vocabulary, so a tile can never show a badge that contradicts its clock.
 */
export const LotStateBadge: React.FC<{ state: LotState; className?: string; closedStatus?: ClosedLotStatus }> = ({
  state,
  className = '',
  closedStatus = 'closed',
}) => {
  const base = 'text-xs font-bold px-2 py-1 rounded';

  if (state.kind === 'closed_sold' || state.kind === 'closed_unsold') {
    // closedStatus: see closedLotStatus in saleState.ts. Display only.
    const tone = closedStatus === 'to-be-relisted' ? 'bg-orange-600 text-white'
      : closedStatus === 'under-review' ? 'bg-amber-500 text-gray-900' : 'bg-gray-700 text-white';
    return <span className={`${base} ${tone} ${className}`}>{closedLotWord(closedStatus, 'upper')}</span>;
  }
  if (state.kind === 'settling') {
    return <span className={`${base} bg-red-600 text-white ${className}`}>CLOSING</span>;
  }
  if (state.kind === 'closing_now' || state.kind === 'closing_soon') {
    return (
      <span className={`${base} bg-red-600 text-white animate-pulse ${className}`}>
        {state.extended ? `EXTENDED ×${state.extensionCount}` : 'CLOSING'}
      </span>
    );
  }
  return null;
};

export default LotClock;
